import {
  SHELF_AUDIT_HEIGHT_BANDS,
  SHELF_AUDIT_HEIGHT_LIMITS,
  type ShelfAuditHeightLimit,
} from '@/shared/types/shelfAudit.types';
import {
  compareHighShelfAuditPath,
  compareShelfNamesNaturally,
} from '@/server/utils/highShelfAuditPath';

export interface ScanTrailShelf {
  readonly id: number;
  readonly shelf_name: string;
  readonly performed_by?: string | null;
}

/** Same height rule as claim-next: the last shelf-index segment. */
function shelfMatchesHeightLimit(
  shelfName: string,
  limit: ShelfAuditHeightLimit | null,
): boolean {
  if (limit == null) return true;
  const last = shelfName.trim().split('-').pop() ?? '';
  if (!/^\d+$/.test(last)) return false;
  const height = Number(last);
  const band = SHELF_AUDIT_HEIGHT_BANDS[limit];
  return height >= band.min && height <= band.max;
}

/**
 * Shelves this user already scanned, in walk order, then the assigned frontier.
 * Shelves after the frontier, scanned by someone else, or outside the height band are omitted.
 */
export function buildShelfAuditScanTrail<T extends ScanTrailShelf>(input: {
  shelves: readonly T[];
  heightLimit: ShelfAuditHeightLimit | null;
  performedBy: string;
  frontierId: number;
}): T[] {
  const email = input.performedBy.trim().toLowerCase();
  const frontier = input.shelves.find((shelf) => shelf.id === input.frontierId);
  if (!frontier || !email) return frontier ? [frontier] : [];

  const compare = input.heightLimit === SHELF_AUDIT_HEIGHT_LIMITS.high
    ? compareHighShelfAuditPath
    : compareShelfNamesNaturally;

  const ordered = input.shelves
    .filter((shelf) => shelfMatchesHeightLimit(shelf.shelf_name, input.heightLimit))
    .sort((a, b) => compare(a.shelf_name, b.shelf_name));
  const frontierPos = ordered.findIndex((shelf) => shelf.id === frontier.id);

  const backlog = ordered.filter((shelf, index) => {
    if (shelf.id === frontier.id) return false;
    if (frontierPos >= 0 && index > frontierPos) return false;
    return (shelf.performed_by || '').trim().toLowerCase() === email;
  });

  return [...backlog, frontier];
}
